import {
  CreateButton,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";
import { CopyableEmailTag } from "components/fields/CopyableEmailTag";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { IShipco, IVessel } from "interfaces";
import prettyBytes from "pretty-bytes";
import React from "react";

export const VesselList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IVessel>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } = useExport<IVessel>({
    mapData: (item) => {
      return {
        "Vessel Name": item?.name || "",
        "Vessel Type": item?.type || "",
        "IMO Number": item?.imoNumber || "",
        "Communication Email": item?.email || "",
        "Call Sign": item?.callSign || "",
        "EPS System ID": item?.epsSystemId || "",
        "Hold Status": item?.holdStatus || "",
        "UKHO Licence ID": item?.licence?.ukhoLicenceId || "",
        "Shipco": item?.licence?.shipco?.registrationName || "",
        "Shipco Group": item?.licence?.shipcoGroup?.registrationName || "",
      };
    },
    unparseConfig: {
      header: true,
      columns: [
        "Vessel Name",
        "Vessel Type",
        "IMO Number",
        "Communication Email",
        "Call Sign",
        "EPS System ID",
        "Hold Status",
        "UKHO Licence ID",
        "Shipco",
        "Shipco Group",
      ],
    },
  });

  const { selectProps: vesselNameSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",

    pagination: {
      mode: "server"
    }
  });

  const { selectProps: shipcoSelectProps } = useSelect<IShipco>({
    resource: "shipco",
    optionLabel: "registrationName",
    optionValue: "registrationName",

    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IVessel> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselNameSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      width: 120,
      render: (value) => <TextField value={value} />,
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Communication Email",
      dataIndex: "email",
      key: "email",
      width: 220,
      className: "wrap-text",
      render: (value) => <CopyableEmailTag value={value} />,
    },
    {
      title: "Shipco",
      dataIndex: ["licence", "shipco", "registrationName"],
      key: "vesselShipco.registrationName",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Shipco"
            {...shipcoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Max Email Size",
      dataIndex: "maxEmailSizeKb",
      key: "maxEmailSizeKb",
      width: 130,
      render: (value) => <TextField value={value ? prettyBytes(value) : null} />,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        subTitle: "active vessels",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
